import React from 'react';
import { useTranslation } from 'react-i18next';
import type { SWRConfiguration } from 'swr';
import { mutate as globalMutate } from 'swr';

import type {
  FolderOrItem,
  StructureNodes,
} from 'components/FolderStructure/types';
import { useApi } from 'data/base-api';
import useHandleError from 'hooks/useHandleError';
import useShowAlert from 'hooks/useShowAlert';
import { checkStatus, del, get, patch, post, put } from 'libs/apiClient';
import type { InfoItem, NewInfoPageFolder } from 'model/info-center';
import { isInfoMessage, MessageType, MessageView } from 'model/info-center';

const baseUrl = '/api/v3/infocenter';
const infoPagesStructureUrl = `${baseUrl}/info-pages/structure?view=30`;
const params = new URLSearchParams();
params.append('view', String(MessageView.AdminWeb));

const isStructureNodeData = (data: InfoItem[] | StructureNodes) =>
  'rootNodes' in data;

export const useCreateInfoItem = () => {
  const handleError = useHandleError();

  return async (
    item: Partial<InfoItem> & { parentFolderId?: string | null }
  ) => {
    let newItem: InfoItem | null = null;
    const isPage = item.messageType === MessageType.InfoPage;

    await globalMutate<InfoItem[] | StructureNodes>(
      (key) =>
        typeof key === 'string' &&
        key.startsWith(baseUrl) &&
        (isPage ? key.includes(infoPagesStructureUrl) : key.includes('all')),
      async (data) => {
        try {
          const response = checkStatus(await post(baseUrl, item));

          const newInfoItem: InfoItem = await response.json();
          newItem = newInfoItem;

          globalMutate<InfoItem[]>(
            (key) =>
              typeof key === 'string' && key.startsWith(`${baseUrl}/all`),
            (listData) => {
              if (listData) {
                return listData.map((i) =>
                  i.id === item.id ? newInfoItem : i
                );
              }

              return [newInfoItem];
            }
          );

          return data;
        } catch (e) {
          handleError(e);

          return data;
        }
      },
      {
        revalidate: true,
      }
    );

    return newItem as InfoItem | null;
  };
};

export const useCreateInfoPageFolder = () => {
  const handleError = useHandleError();

  return async (item: NewInfoPageFolder) => {
    let newFolderId = '';

    try {
      const response = checkStatus(await post(baseUrl, item));
      const parsed = (await response.json()) as InfoItem;
      newFolderId = parsed.id;

      globalMutate(infoPagesStructureUrl);
    } catch (e) {
      handleError(e);
    }

    return newFolderId;
  };
};

export const useUpdateInfoItem = () => {
  const { t } = useTranslation();

  const handleError = useHandleError();

  const { showSuccessMessage } = useShowAlert();

  return async (item: InfoItem) => {
    let updatedItem: InfoItem | null = null;
    const isPage = item.messageType === MessageType.InfoPage;

    try {
      const response = checkStatus(await put(`${baseUrl}/${item.id}`, item));

      showSuccessMessage({
        message:
          item.messageType === MessageType.InfoPage
            ? t('InfoCenter.InfoPages.Success')
            : t('InfoCenter.Messages.Success'),
      });

      const parsed: InfoItem = await response.json();

      updatedItem = {
        ...item,
        ...parsed,
      };

      if (updatedItem) {
        // Update the all cache so consumers of useInfocenterAllRaw get fresh data
        globalMutate<InfoItem[]>(
          (key) => typeof key === 'string' && key.startsWith(`${baseUrl}/all`),
          (data) => {
            if (data) {
              return data.map((i) => (i.id === item.id ? updatedItem! : i));
            }

            return [updatedItem!];
          },
          {
            revalidate: false,
          }
        );

        if (isPage) {
          await globalMutate<StructureNodes>(
            (key) =>
              typeof key === 'string' &&
              key.startsWith(baseUrl) &&
              key.includes(infoPagesStructureUrl),
            async (data) => {
              if (updatedItem) {
                if (data) {
                  const replaceNodeRecursively = (
                    nodes: typeof data.rootNodes
                  ): typeof data.rootNodes =>
                    nodes.map((node) => {
                      if (node.id === item.id) {
                        return {
                          ...node,
                          name: updatedItem!.title,
                          title: updatedItem!.title,
                          messageVisibilityLevel:
                            updatedItem!.messageVisibilityLevel,
                          userIds: updatedItem!.userIds,
                          visibleForCompanyIds:
                            updatedItem!.visibleForCompanyIds,
                          tagIds: updatedItem!.tagIds,
                        };
                      }

                      if (node.children && node.children.length > 0) {
                        return {
                          ...node,
                          children: replaceNodeRecursively(node.children),
                        };
                      }

                      return node;
                    });

                  return {
                    rootNodes: replaceNodeRecursively(data.rootNodes),
                  };
                }

                return { rootNodes: [{ ...updatedItem, isFolder: false }] };
              }

              return { rootNodes: [] };
            },
            {
              revalidate: false,
              optimisticData: (data) => {
                if (!data) {
                  return { rootNodes: [] };
                }

                const replaceNodeRecursively = (
                  nodes: typeof data.rootNodes
                ): typeof data.rootNodes =>
                  nodes.map((node) => {
                    if (node.id === item.id) {
                      return {
                        ...node,
                        title: item.title,
                        messageVisibilityLevel: item.messageVisibilityLevel,
                        userIds: item.userIds,
                        visibleForCompanyIds: item.visibleForCompanyIds,
                        tagIds: item.tagIds,
                      };
                    }

                    if (node.children && node.children.length > 0) {
                      return {
                        ...node,
                        children: replaceNodeRecursively(node.children),
                      };
                    }

                    return node;
                  });

                return {
                  ...data,
                  rootNodes: replaceNodeRecursively(data.rootNodes),
                };
              },
            }
          );
        } else {
          globalMutate<InfoItem[]>(
            (key) =>
              typeof key === 'string' &&
              key.startsWith(baseUrl) &&
              key.includes('all'),
            (data) => {
              if (data) {
                return data.map((i) => (i.id === item.id ? updatedItem! : i));
              }

              return [updatedItem!];
            },
            {
              revalidate: false,
              optimisticData: (data) => {
                if (!data) {
                  return [];
                }

                return (
                  data?.map((i) =>
                    i.id === item.id
                      ? {
                          ...i,
                          ...item,
                        }
                      : i
                  ) || []
                );
              },
            }
          );
        }
      }

      return updatedItem as InfoItem;
    } catch (e) {
      handleError(e);

      return null;
    }
  };
};

export const useInfocenterAllRaw = (
  search?: string,
  config?: SWRConfiguration
) => {
  const handleError = useHandleError();
  const { data, error, mutate } = useApi<InfoItem[]>(
    `${baseUrl}/all?${params.toString()}${search || ''}`,
    config
  );

  React.useEffect(() => {
    if (error) {
      handleError(error);
    }
  }, [error]);

  const { messages, pages } = React.useMemo(() => {
    const p: InfoItem[] = [];
    const m: InfoItem[] = [];

    if (data) {
      data.forEach((item) => {
        if (isInfoMessage(item)) {
          m.push(item);
        } else {
          p.push(item);
        }
      });
    }

    return {
      messages: m,
      pages: p,
    };
  }, [data]);

  return {
    infoMessages: messages,
    infoPages: pages,
    mutate,
    isFetching: !data && !error,
  };
};

export const useInfoPagesStructure = (SWRConfig?: SWRConfiguration) =>
  useApi<StructureNodes>(infoPagesStructureUrl, SWRConfig);

export const useGetInfoPagesByCompanyId = (companyId: string | undefined) =>
  useApi<StructureNodes>(
    companyId ? `${baseUrl}/info-pages/structure/company/${companyId}` : null
  );

export const useInfoCenterDetail = (id?: string) => {
  const { data, ...rest } = useApi<InfoItem>(
    id ? `${baseUrl}/${id}?${params.toString()}` : undefined
  );

  return {
    data,
    ...rest,
  } as const;
};

export const useMoveMessageNode = () => {
  const { t } = useTranslation();
  const handleError = useHandleError();
  const { showSuccessMessage } = useShowAlert();

  return async (
    item: FolderOrItem,
    isFolder: boolean,
    destinationId: string | null,
    destinationName: string
  ) => {
    const url = `${baseUrl}/info-pages/structure`;

    try {
      const response = checkStatus(
        await patch(url, {
          newParentNodeId: destinationId,
          nodeIdsToMove: [item.id],
        })
      );
      globalMutate(infoPagesStructureUrl, await response.json());
      showSuccessMessage({
        message: isFolder
          ? t('InfoCenter.FolderMoved', {
              name: item.name || item.title,
              destinationName,
            })
          : t('InfoCenter.PageMoved', {
              name: item.name || item.title,
              destinationName,
            }),
      });
    } catch (e) {
      handleError(e);
    }
  };
};

export const useDeleteRequest = () => {
  const { t } = useTranslation();

  const handleError = useHandleError();

  const { showSuccessMessage } = useShowAlert();

  return async (id: string, name: string, isPage = false) => {
    globalMutate<InfoItem[] | StructureNodes>(
      (key) =>
        typeof key === 'string' &&
        key.startsWith(baseUrl) &&
        (isPage ? key.includes(infoPagesStructureUrl) : key.includes('all')),
      async (data) => {
        try {
          checkStatus(await del(`${baseUrl}/${id}`));

          showSuccessMessage({
            message: t('FolderStructure.DeleteDialog.Deleted', {
              name,
            }),
          });

          if (!data) {
            if (isPage) {
              return {
                rootNodes: [],
              };
            }

            return [];
          }

          if (isStructureNodeData(data)) {
            const filterNodesRecursively = (
              nodes: typeof data.rootNodes
            ): typeof data.rootNodes =>
              nodes
                .filter((node) => node.id !== id)
                .map((node) => ({
                  ...node,
                  children: node.children
                    ? filterNodesRecursively(node.children)
                    : node.children,
                }));

            return {
              ...data,
              rootNodes: filterNodesRecursively(data.rootNodes),
            };
          }

          return data?.filter((item) => item.id !== id) || [];
        } catch (e) {
          handleError(e);

          return data;
        }
      },
      {
        revalidate: false,
        optimisticData: (data) => {
          if (!data) {
            if (isPage) {
              return {
                rootNodes: [],
              };
            }

            return [];
          }

          if (isStructureNodeData(data)) {
            const filterNodesRecursively = (
              nodes: typeof data.rootNodes
            ): typeof data.rootNodes =>
              nodes
                .filter((node) => node.id !== id)
                .map((node) => ({
                  ...node,
                  children: node.children
                    ? filterNodesRecursively(node.children)
                    : node.children,
                }));

            return {
              ...data,
              rootNodes: filterNodesRecursively(data.rootNodes),
            };
          }

          return data?.filter((item) => item.id !== id) || [];
        },
      }
    );
  };
};

export const useSendPushMessage = () => {
  const { t } = useTranslation();

  const handleError = useHandleError();

  const { showSuccessMessage } = useShowAlert();

  return async (id: string, future: boolean, isPage: boolean) => {
    try {
      checkStatus(await patch(`${baseUrl}/sendPush/${id}`));

      if (isPage) {
        showSuccessMessage({
          message: future
            ? t('InfoCenter.PagesPushDialog.SuccessInitiated')
            : t('InfoCenter.PagesPushDialog.Success'),
        });
      } else {
        showSuccessMessage({
          message: future
            ? t('InfoCenter.PushDialog.SuccessInitiated')
            : t('InfoCenter.PushDialog.Success'),
        });
      }

      globalMutate<InfoItem[] | StructureNodes>(
        (key) =>
          typeof key === 'string' &&
          key.startsWith(baseUrl) &&
          (isPage ? key.includes(infoPagesStructureUrl) : key.includes('all'))
      );
    } catch (e) {
      handleError(e);
    }
  };
};

export const useEditName = () => {
  const handleError = useHandleError();

  return async (id: string, title: string) => {
    await globalMutate<StructureNodes>(
      (key) =>
        typeof key === 'string' &&
        key.startsWith(baseUrl) &&
        key.includes(infoPagesStructureUrl),
      async (data) => {
        try {
          const searchParams = new URLSearchParams({ title });
          const response = checkStatus(
            await patch(
              `${baseUrl}/info-pages/structure/edit-title/${id}?${searchParams.toString()}`
            )
          );
          const parsed = (await response.json()) as StructureNodes;

          return parsed;
        } catch (e) {
          handleError(e);

          return data || { rootNodes: [] };
        }
      },
      {
        revalidate: false,
        optimisticData: (data) => {
          if (!data) return { rootNodes: [] };

          const updateNodeInTree = (nodes: FolderOrItem[]): FolderOrItem[] =>
            nodes.map((node) => {
              if (node.id === id) {
                return { ...node, name: title };
              }

              if (node.children && node.children.length > 0) {
                return {
                  ...node,
                  children: updateNodeInTree(node.children),
                };
              }

              return node;
            });

          return {
            ...data,
            rootNodes: updateNodeInTree(data.rootNodes),
          };
        },
      }
    );
    globalMutate(
      (key) =>
        typeof key === 'string' &&
        key.startsWith(baseUrl) &&
        key.includes('all')
    );
  };
};

export const useSysopCopyInfoPage = () => {
  const { t } = useTranslation();
  const handleError = useHandleError();
  const { showSuccessMessage } = useShowAlert();

  return async (
    infoPages: { id: string }[],
    copyFromCompanyId: string,
    copyToCompanyId: string,
    numberOfPages: number,
    companyName: string
  ) => {
    try {
      checkStatus(
        await post(`${baseUrl}/info-pages/copy-to-company`, {
          infoPagesToCopy: infoPages,
          copyFromCompanyId,
          copyToCompanyId,
        })
      );
      showSuccessMessage({
        message: t('SysOp.InfoPageCopying.InfoPagesCopiedToCompany', {
          count: numberOfPages,
          companyName,
        }),
      });
    } catch (e) {
      handleError(e);
    }
  };
};

export const useDuplicateInfoPage = () => {
  const handleError = useHandleError();

  return async (
    infoPages: { id: string; copyTitle?: string }[],
    companyId: string
  ) => {
    try {
      checkStatus(
        await post(`${baseUrl}/info-pages/copy`, {
          infoPagesToCopy: infoPages,
          copyFromCompanyId: companyId,
          copyToCompanyId: companyId,
        })
      );
      globalMutate(infoPagesStructureUrl);
    } catch (e) {
      handleError(e);
    }
  };
};

export const downloadFeedback = async (messageId: string): Promise<Blob> => {
  const response = await get(`${baseUrl}/feedback/download/${messageId}`);
  return response.blob();
};

export const useGetGeneratePublicURL = (messageId: string | undefined) => {
  const { data, error, ...rest } = useApi<string>(
    messageId ? `${baseUrl}/public-url/${messageId}` : null
  );

  let url = data?.trim() || '';

  if (url.startsWith('"') && url.endsWith('"')) {
    url = url.slice(1, -1);
  }

  return {
    publicUrl: url,
    error,
    ...rest,
  };
};

export const useGetPublicInfoMessageById = (
  infoMessageId: string | undefined,
  xai: string | undefined,
  xat: string | undefined
) => {
  if (xai) params.set('xai', xai);
  if (xat) params.set('xat', xat);

  const { data, ...rest } = useApi<InfoItem>(
    infoMessageId && xai && xat
      ? `${baseUrl}/public-info-message/${infoMessageId}?${params}`
      : null
  );

  return {
    infoMessage: data,
    ...rest,
  };
};
